

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/base/Button';

export default function Dashboard() {
  const [clientData, setClientData] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const data = localStorage.getItem('clientData');
    if (data) {
      setClientData(JSON.parse(data));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('clientData');
    navigate('/');
  };

  const projects = [
    {
      id: 1,
      name: 'Backyard Renovation',
      status: 'In Progress',
      progress: 75,
      startDate: '2024-01-15',
      estimatedCompletion: '2024-02-28',
      type: 'Landscape'
    },
    {
      id: 2,
      name: 'Kitchen Addition',
      status: 'Planning',
      progress: 25,
      startDate: '2024-03-01',
      estimatedCompletion: '2024-05-15',
      type: 'Construction'
    },
    {
      id: 3,
      name: 'Front Yard Landscaping',
      status: 'Completed',
      progress: 100,
      startDate: '2023-11-01',
      estimatedCompletion: '2023-12-15',
      type: 'Landscape'
    }
  ];

  const recentActivity = [
    {
      id: 1,
      date: '2024-01-20',
      activity: 'Project update: Backyard Renovation - Patio installation completed',
      type: 'update'
    },
    {
      id: 2,
      date: '2024-01-18',
      activity: 'Invoice #2024-001 generated for $2,500',
      type: 'billing'
    },
    {
      id: 3,
      date: '2024-01-15',
      activity: 'New photos added to Backyard Renovation portfolio',
      type: 'portfolio'
    },
    {
      id: 4,
      date: '2024-01-10',
      activity: 'Payment received for Invoice #2023-012',
      type: 'payment'
    }
  ];

  if (!clientData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <div className="w-10 h-10 bg-red-700 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-hammer-line text-white"></i>
              </div>
              <h1 className="text-xl font-bold text-red-700" style={{ fontFamily: '"Pacifico", serif' }}>
                Chavez Castro
              </h1>
            </Link>
            
            <nav className="flex space-x-6">
              <Link to="/client/dashboard" className="text-red-700 font-medium border-b-2 border-red-700">
                Dashboard
              </Link>
              <Link to="/client/portfolio" className="text-gray-600 hover:text-red-700">
                My Portfolio
              </Link>
              <Link to="/client/billing" className="text-gray-600 hover:text-red-700">
                Billing
              </Link>
            </nav>

            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {clientData.name}</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {clientData.name}!</h1>
          <p className="text-gray-600">Here's an overview of your projects and account status.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-folder-line text-blue-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Projects</p>
                <p className="text-2xl font-bold text-gray-900">2</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-check-line text-green-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">1</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-bill-line text-yellow-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Bills</p>
                <p className="text-2xl font-bold text-gray-900">$2,500</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-image-line text-red-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Portfolio Items</p>
                <p className="text-2xl font-bold text-gray-900">15</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Current Projects */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Current Projects</h2>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {projects.map((project) => (
                  <div key={project.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{project.name}</h3>
                        <p className="text-sm text-gray-600">{project.type}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        project.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        project.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-600 h-2 rounded-full" 
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <p>Started: {new Date(project.startDate).toLocaleDateString()}</p>
                      <p>Est. Completion: {new Date(project.estimatedCompletion).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                      activity.type === 'update' ? 'bg-blue-100' :
                      activity.type === 'billing' ? 'bg-yellow-100' :
                      activity.type === 'portfolio' ? 'bg-purple-100' :
                      'bg-green-100'
                    }`}>
                      <i className={`text-sm ${
                        activity.type === 'update' ? 'ri-information-line text-blue-600' :
                        activity.type === 'billing' ? 'ri-bill-line text-yellow-600' :
                        activity.type === 'portfolio' ? 'ri-image-line text-purple-600' :
                        'ri-money-dollar-circle-line text-green-600'
                      }`}></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.activity}</p>
                      <p className="text-xs text-gray-500">{new Date(activity.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/client/portfolio">
              <Button variant="outline" className="w-full">
                <i className="ri-image-line mr-2"></i>
                View Portfolio
              </Button>
            </Link>
            <Link to="/client/billing">
              <Button variant="outline" className="w-full">
                <i className="ri-bill-line mr-2"></i>
                Pay Bills
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="w-full">
                <i className="ri-message-line mr-2"></i>
                Contact Support
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

